/* FlashReconciliation.jsx
 *
 * Daily audit of the Flash VAS machine. The cashier types in the "Total
 * Sales" off the End-of-Shift slip; we compute the POS-side total
 * automatically and surface the variance.
 *
 * Designed to take ~5 seconds per day — no file uploads.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Smartphone, Zap, CheckCircle2, AlertTriangle, Calendar, Save, RefreshCw, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const fmtZAR = (n) => "R " + (Number(n || 0)).toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const todaySAST = () => {
  const now = new Date();
  const sast = new Date(now.getTime() + (2 * 60 - now.getTimezoneOffset()) * 60000);
  return sast.toISOString().slice(0, 10);
};

export default function FlashReconciliation() {
  const [date, setDate] = useState(todaySAST());
  const [posSummary, setPosSummary] = useState(null);
  const [machineTotal, setMachineTotal] = useState("");
  const [shiftNumber, setShiftNumber] = useState("");
  const [notes, setNotes] = useState("");
  const [existing, setExisting] = useState(null);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadEverything = async (d) => {
    setBusy(true);
    try {
      const [pos, byDate, hist] = await Promise.all([
        axios.get(`${API}/api/flash-reconciliation/pos-summary`, { params: { date: d } }),
        axios.get(`${API}/api/flash-reconciliation/by-date`, { params: { date: d } }),
        axios.get(`${API}/api/flash-reconciliation/list`, { params: { days: 30 } }),
      ]);
      setPosSummary(pos.data);
      if (byDate.data) {
        setExisting(byDate.data);
        setMachineTotal(byDate.data.flash_machine_total.toString());
        setShiftNumber(byDate.data.shift_number || "");
        setNotes(byDate.data.notes || "");
      } else {
        setExisting(null);
        setMachineTotal("");
        setShiftNumber("");
        setNotes("");
      }
      setHistory(hist.data.rows || []);
    } catch (e) {
      toast.error("Failed to load reconciliation data");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { loadEverything(date); /* eslint-disable-next-line */ }, [date]);

  const variance = useMemo(() => {
    if (machineTotal === "" || posSummary === null) return null;
    return Number(machineTotal) - Number(posSummary.total_amount || 0);
  }, [machineTotal, posSummary]);

  const status = useMemo(() => {
    if (variance === null) return null;
    if (Math.abs(variance) <= 0.05) return "match";
    // variance > 0 → Flash > POS → POS short (missed ring / theft)
    // variance < 0 → POS > Flash → POS over (over-rang / data entry)
    return variance > 0 ? "short" : "over";
  }, [variance]);

  const save = async () => {
    if (machineTotal === "") {
      toast.error("Enter the Flash machine total before saving");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/api/flash-reconciliation/save`, {
        date,
        flash_machine_total: Number(machineTotal),
        pos_total: Number(posSummary?.total_amount || 0),
        shift_number: shiftNumber || null,
        notes: notes || null,
      });
      toast.success("Flash reconciliation saved");
      await loadEverything(date);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto" data-testid="flash-reconciliation-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Smartphone className="h-6 w-6 text-indigo-600" />
            Flash VAS — Daily Reconciliation
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Match what the Flash machine sold against what was rung up at the till.
            Catches missed sales, theft, and shortcuts. ~5 seconds per day.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <Label className="text-xs">Date (SAST)</Label>
            <div className="flex items-center gap-1">
              <Calendar className="h-4 w-4 text-gray-400" />
              <Input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                max={todaySAST()}
                className="w-44"
                data-testid="recon-date-input"
              />
            </div>
          </div>
          <Button variant="outline" size="icon" onClick={() => loadEverything(date)} disabled={busy} title="Refresh">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      {/* Variance banner */}
      {status && (
        <div className={`rounded-lg p-4 flex items-center justify-between flex-wrap gap-3 border-2 ${
          status === "match" ? "bg-green-50 border-green-300" :
          status === "short" ? "bg-red-50 border-red-300" :
          "bg-amber-50 border-amber-300"
        }`}>
          <div className="flex items-center gap-3">
            {status === "match" ? (
              <CheckCircle2 className="h-8 w-8 text-green-600" />
            ) : (
              <AlertTriangle className={`h-8 w-8 ${status === "short" ? "text-red-600" : "text-amber-600"}`} />
            )}
            <div>
              <div className={`font-bold text-lg ${
                status === "match" ? "text-green-800" :
                status === "short" ? "text-red-800" :
                "text-amber-800"
              }`} data-testid="variance-status">
                {status === "match" && "Match — every Flash sale was rung up correctly."}
                {status === "short" && `SHORT by ${fmtZAR(Math.abs(variance))} — Flash sold more than the POS rang up.`}
                {status === "over" && `OVER by ${fmtZAR(variance)} — POS rang up more than the Flash machine sold.`}
              </div>
              {status === "short" && (
                <div className="text-sm text-red-700 mt-0.5">
                  Likely missed ring or theft — check the day's till activity.
                </div>
              )}
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs text-gray-600 uppercase">Variance</div>
            <div className={`text-2xl font-bold ${
              status === "match" ? "text-green-700" :
              status === "short" ? "text-red-700" :
              "text-amber-700"
            }`}>{variance >= 0 ? "+" : "−"}{fmtZAR(Math.abs(variance))}</div>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        {/* POS SIDE */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Zap className="h-4 w-4 text-blue-600" />
              POS side — what was rung up
            </CardTitle>
            <p className="text-xs text-gray-500">
              Auto-calculated from till transactions for {date}.
            </p>
          </CardHeader>
          <CardContent>
            {busy && !posSummary && (
              <div className="flex items-center gap-2 text-gray-500 py-6">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading…
              </div>
            )}
            {posSummary && (
              <>
                <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-3">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-blue-900">Total Flash service revenue</div>
                    <div className="text-2xl font-bold text-blue-700" data-testid="pos-total">
                      {fmtZAR(posSummary.total_amount)}
                    </div>
                  </div>
                  <div className="text-xs text-blue-700/80 mt-1">
                    {posSummary.total_count} item{posSummary.total_count === 1 ? "" : "s"} across all till transactions.
                  </div>
                  {Number(posSummary.refund_amount || 0) > 0 && (
                    <div className="mt-2 pt-2 border-t border-blue-200 text-xs text-blue-900/80" data-testid="pos-refund-breakdown">
                      <div className="flex justify-between">
                        <span>Flash refunds today (info only, not subtracted)</span>
                        <span className="font-mono">{fmtZAR(posSummary.refund_amount)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Net-of-refunds (informational)</span>
                        <span className="font-mono">{fmtZAR(posSummary.net_amount)}</span>
                      </div>
                    </div>
                  )}
                </div>

                {posSummary.categories.length === 0 ? (
                  <p className="text-sm text-gray-400 italic py-4 text-center">
                    No Flash service sales recorded on this date.
                  </p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Category</TableHead>
                        <TableHead className="text-right">Items</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {posSummary.categories.map((c, idx) => (
                        <TableRow key={idx} data-testid={`pos-cat-${idx}`}>
                          <TableCell className="font-medium">{c.category}</TableCell>
                          <TableCell className="text-right">{c.count}</TableCell>
                          <TableCell className="text-right">{fmtZAR(c.amount)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* FLASH MACHINE SIDE */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Smartphone className="h-4 w-4 text-indigo-600" />
              Flash machine — End-of-Shift Total
            </CardTitle>
            <p className="text-xs text-gray-500">
              Type the <strong>Total Sales</strong> from the Flash slip. Just one number.
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-sm">Flash machine "Total Sales" (R)</Label>
              <Input
                type="number"
                step="0.01"
                inputMode="decimal"
                placeholder="e.g. 2400.03"
                value={machineTotal}
                onChange={(e) => setMachineTotal(e.target.value)}
                className="text-2xl h-14 text-right font-mono"
                data-testid="machine-total-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Shift # (optional)</Label>
                <Input
                  value={shiftNumber}
                  onChange={(e) => setShiftNumber(e.target.value)}
                  placeholder="e.g. 291"
                  data-testid="shift-number-input"
                />
              </div>
              <div>
                <Label className="text-xs">Notes (optional)</Label>
                <Input
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="e.g. checked OK"
                  data-testid="notes-input"
                />
              </div>
            </div>

            <Button
              onClick={save}
              disabled={saving || machineTotal === ""}
              className="w-full h-11 text-base bg-indigo-600 hover:bg-indigo-700"
              data-testid="save-recon-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              {existing ? "Update reconciliation" : "Save reconciliation"}
            </Button>

            {existing && (
              <p className="text-xs text-gray-500 italic">
                Saved {new Date(existing.saved_at).toLocaleString("en-ZA")}
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* HISTORY */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Past 30 days</CardTitle>
        </CardHeader>
        <CardContent>
          {history.length === 0 ? (
            <p className="text-sm text-gray-400 italic py-4 text-center">No previous reconciliations.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Shift</TableHead>
                  <TableHead className="text-right">POS</TableHead>
                  <TableHead className="text-right">Flash machine</TableHead>
                  <TableHead className="text-right">Variance</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Notes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {history.map((r) => (
                  <TableRow key={r.id} className={date === r.date ? "bg-indigo-50" : ""}>
                    <TableCell className="font-medium">
                      <button className="text-indigo-700 hover:underline" onClick={() => setDate(r.date)}>
                        {r.date}
                      </button>
                    </TableCell>
                    <TableCell>{r.shift_number || "—"}</TableCell>
                    <TableCell className="text-right">{fmtZAR(r.pos_total)}</TableCell>
                    <TableCell className="text-right">{fmtZAR(r.flash_machine_total)}</TableCell>
                    <TableCell className={`text-right font-medium ${
                      r.status === "match" ? "text-green-700" :
                      r.status === "short" ? "text-red-700" : "text-amber-700"
                    }`}>
                      {r.variance >= 0 ? "+" : "−"}{fmtZAR(Math.abs(r.variance))}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={
                        r.status === "match" ? "border-green-400 text-green-700" :
                        r.status === "short" ? "border-red-400 text-red-700" :
                        "border-amber-400 text-amber-700"
                      }>
                        {r.status === "match" ? "Match" : r.status === "short" ? "Short" : "Over"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-gray-500 truncate max-w-[160px]" title={r.notes}>
                      {r.notes || "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
